import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { Calculator, ChevronRight, ChevronLeft, Printer, Save, Trash2, RefreshCw, CheckCircle, Plus, FlaskConical } from 'lucide-react';
import { toast } from 'react-toastify';

// ── Constants ──────────────────────────────────────────────
const TANK_TYPES = [
  { value: 'vertical_fixed_roof',     label: 'Vertical Fixed Roof',         desc: 'Cylindrical shell with cone, dome, or flat permanent roof.' },
  { value: 'horizontal_fixed_roof',   label: 'Horizontal Fixed Roof',       desc: 'Axis parallel to foundation — above or underground.' },
  { value: 'internal_floating',       label: 'Internal Floating Roof (IFR)',desc: 'Fixed outer roof with internal floating cover.' },
  { value: 'external_floating',       label: 'External Floating Roof (EFR)',desc: 'Open-top cylindrical shell with floating roof.' },
  { value: 'domed_external_floating', label: 'Domed External Floating Roof',desc: 'EFR retrofitted with a dome fixed roof.' },
];

const COLORS = [
  { value: 'white', label: 'White' },
  { value: 'beige', label: 'Beige' },
  { value: 'aluminum', label: 'Aluminum/Silver' },
  { value: 'light_gray', label: 'Light Gray' },
  { value: 'gray', label: 'Gray' },
  { value: 'medium_gray', label: 'Medium Gray' },
  { value: 'dark_gray', label: 'Dark Gray' },
  { value: 'light_green', label: 'Light Green' },
  { value: 'dark_green', label: 'Dark Green' },
  { value: 'red', label: 'Red' },
  { value: 'brown', label: 'Brown' },
  { value: 'black', label: 'Black' },
  { value: 'unpainted_steel', label: 'Unpainted Steel' },
];

const CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'average', label: 'Average' },
  { value: 'aged', label: 'Aged' },
];

const INTERNAL_SHELL_CONDITIONS = [
  { value: 'light_rust', label: 'Light Rust' },
  { value: 'dense_rust', label: 'Dense Rust' },
  { value: 'gunite_lining', label: 'Gunite Lining (Sludge-lined)' },
];

const PRIMARY_SEALS_IFR = [
  { value: 'mechanical_shoe', label: 'Mechanical Shoe Seal' },
  { value: 'liquid_mounted', label: 'Liquid-Mounted Seal' },
  { value: 'vapor_mounted', label: 'Vapor-Mounted Seal' },
];

const PRIMARY_SEALS_EFR_WELDED = [
  { value: 'mechanical_shoe', label: 'Mechanical Shoe Seal' },
  { value: 'liquid_mounted', label: 'Liquid-Mounted Seal' },
  { value: 'vapor_mounted', label: 'Vapor-Mounted Seal' },
];

const PRIMARY_SEALS_EFR_RIVETED = [
  { value: 'mechanical_shoe', label: 'Mechanical Shoe Seal' },
];

const SECONDARY_SEALS_IFR = [
  { value: 'none', label: 'None' },
  { value: 'rim_mounted', label: 'Rim-Mounted Secondary Seal' },
];

const SECONDARY_SEALS_EFR = [
  { value: 'none', label: 'None' },
  { value: 'weather_shield', label: 'Weather Shield' },
  { value: 'rim_mounted', label: 'Rim-Mounted Secondary Seal' },
];

const DECK_FITTINGS = [
  { key: 'access_hatch',              label: 'Access Hatch' },
  { key: 'fixed_roof_support_column', label: 'Fixed Roof Support Column Well' },
  { key: 'unslotted_guidepole',       label: 'Unslotted Guidepole and Well' },
  { key: 'slotted_guidepole',         label: 'Slotted Guidepole / Sample Well' },
  { key: 'gauge_float_well',          label: 'Gauge-Float Well (Automatic Gauge)' },
  { key: 'gauge_hatch',               label: 'Gauge-Hatch / Sample Port' },
  { key: 'vacuum_breaker',            label: 'Vacuum Breaker' },
  { key: 'deck_drain',                label: 'Deck Drain' },
  { key: 'deck_leg',                  label: 'Deck Leg' },
  { key: 'ladder_well',               label: 'Ladder Well (IFR only)' },
  { key: 'rim_vent',                  label: 'Rim Vent' },
];

const EFR_DECK_FITTINGS = [
  { key: 'access_hatch',         label: 'Access Hatch' },
  { key: 'unslotted_guidepole',  label: 'Unslotted Guidepole and Well' },
  { key: 'slotted_guidepole',    label: 'Slotted Guidepole / Sample Well' },
  { key: 'gauge_float_well',     label: 'Gauge-Float Well' },
  { key: 'gauge_hatch',          label: 'Gauge-Hatch / Sample Port' },
  { key: 'vacuum_breaker',       label: 'Vacuum Breaker' },
  { key: 'deck_drain',           label: 'Deck Drain' },
  { key: 'deck_leg',             label: 'Deck Leg (Pontoon area)' },
  { key: 'rim_vent',             label: 'Rim Vent' },
];

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

// ── Styles ─────────────────────────────────────────────────
const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid #E2E8F0', fontFamily:'var(--font-body)', fontSize:14, boxSizing:'border-box', background:'white' };
const lbl = { fontSize:12, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:4 };
const hint = { fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', marginTop:2 };
const grid2 = { display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14, marginBottom:14 };
const grid3 = { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:14 };

const SectionTitle = ({ children }) => (
  <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12, marginTop:8, paddingBottom:6, borderBottom:'1px solid #F1F5F9' }}>{children}</div>
);

const Field = ({ label, hint: hintText, children }) => (
  <div>
    <label style={lbl}>{label}</label>
    {children}
    {hintText && <div style={hint}>{hintText}</div>}
  </div>
);

// ── Main Component ──────────────────────────────────────────
export default function TankEmissions() {
  const [step, setStep] = useState(1);
  const [facilities, setFacilities] = useState([]);
  const [chemicals, setChemicals] = useState([]);
  const [metCities, setMetCities] = useState([]);
  const [savedCalcs, setSavedCalcs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [chemSearch, setChemSearch] = useState('');
  const [showChemDD, setShowChemDD] = useState(false);
  const [aiLooking, setAiLooking] = useState(false);
  const [aiSourced, setAiSourced] = useState(false);
  const [showCustomChem, setShowCustomChem] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [selectedChem, setSelectedChem] = useState(null);
  const printRef = useRef();

  const EMPTY_FORM = {
    facility_id:'', tank_name:'', tank_type:'',
    // identification
    tank_shape:'cylinder', location_state:'', met_city:'',
    // dimensions
    diameter:'', shell_height:'', shell_length:'',
    max_liquid_height:'', avg_liquid_height:'', min_liquid_height:'',
    bottom_type:'flat', liquid_heel_type:'no_heel', liquid_heel_height:'',
    // fixed roof construction
    roof_type:'cone', roof_slope:'0.0625', roof_radius:'',
    roof_color:'white', roof_condition:'average',
    shell_color:'white', shell_condition:'average',
    // operating
    vapor_space_pressure:'0',
    is_insulated:'not_insulated', is_heated:false,
    bulk_temp_method:'ap42', bulk_temp_rankine:'',
    heat_max_temp:'', heat_avg_temp:'', heat_min_temp:'', heat_cycles:'',
    vacuum_setting:'-0.03', pressure_setting:'0.03',
    has_control_device:false, control_device_efficiency:'',
    // chemical
    chemical_id:'', chemical_name:'',
    molecular_weight:'', liquid_density:'', tvp_at_bulk:'',
    expansion_factor:'', vp_ref_temp:'60',
    input_method:'annual', annual_throughput:'',
    // floating roof
    tank_construction:'welded',
    internal_shell_condition:'light_rust',
    external_shell_color:'white', external_shell_condition:'average',
    roof_color_float:'white', roof_condition_float:'average',
    seal_type:'vapor_mounted', secondary_seal:'none',
    seal_fit:'average', self_supporting_roof:false,
    num_columns:'', column_type:'unknown',
    deck_type:'welded', deck_construction:'sheet', deck_seam:'5ft',
    roof_type_float:'pontoon',
    deck_fittings:{},
    // custom chem
    cc_name:'', cc_cas:'', cc_mw:'', cc_density:'',
    cc_vp:'', cc_vp_temp:'60', cc_expansion:'0.0006',
    cc_tvp_a:'', cc_tvp_b:'', cc_tvp_c:'',
    // met override
    avg_temp:'', max_temp:'', min_temp:'',
    avg_solar_insolation:'', atmospheric_pressure:'',
    notes:'',
  };

  const [form, setForm] = useState(EMPTY_FORM);
  const f = (updates) => setForm(prev => ({ ...prev, ...updates }));

  const isFloating = ['internal_floating','external_floating','domed_external_floating'].includes(form.tank_type);
  const isEFR = ['external_floating','domed_external_floating'].includes(form.tank_type);
  const isIFR = form.tank_type === 'internal_floating';
  const isFixed = ['vertical_fixed_roof','horizontal_fixed_roof'].includes(form.tank_type);
  const isVertical = form.tank_type === 'vertical_fixed_roof';
  const isHorizontal = form.tank_type === 'horizontal_fixed_roof';

  useEffect(() => { loadInit(); }, []);

  const loadInit = async () => {
    try {
      const [facRes, chemRes, calcRes] = await Promise.all([
        api.get('/facilities'),
        api.get('/tanks/chemicals'),
        api.get('/tanks/calculations'),
      ]);
      setFacilities(facRes.data.facilities || []);
      setChemicals(chemRes.data.chemicals || []);
      setSavedCalcs(calcRes.data.calculations || []);
    } catch(e) { console.error(e); }
  };

  const loadMetCities = async (state) => {
    try {
      const res = await api.get(`/tanks/met-cities?state=${state}`);
      setMetCities(res.data.cities || []);
      if (res.data.default_city && !form.met_city) {
        const metRes = await api.get(`/tanks/met-data?city=${encodeURIComponent(res.data.default_city)}`);
        f({ met_city: res.data.default_city, avg_temp: metRes.data.avg, max_temp: metRes.data.max, min_temp: metRes.data.min, avg_solar_insolation: metRes.data.solar, atmospheric_pressure: metRes.data.pressure });
      }
    } catch(e) {}
  };

  const onStateChange = async (state) => {
    f({ location_state: state, met_city: '' });
    await loadMetCities(state);
  };

  const onCityChange = async (city) => {
    f({ met_city: city });
    try {
      const res = await api.get(`/tanks/met-data?city=${encodeURIComponent(city)}`);
      f({ avg_temp: res.data.avg, max_temp: res.data.max, min_temp: res.data.min, avg_solar_insolation: res.data.solar, atmospheric_pressure: res.data.pressure });
    } catch(e) {}
  };

  const filteredChems = chemicals.filter(c =>
    !chemSearch || c.name.toLowerCase().includes(chemSearch.toLowerCase()) || (c.cas_number||'').includes(chemSearch)
  );

  const selectChem = (chem) => {
    setAiSourced(false);
    setSelectedChem(chem);
    f({ chemical_id: chem.id, chemical_name: chem.name, molecular_weight: chem.molecular_weight, liquid_density: chem.liquid_density, tvp_at_bulk: chem.vp_at_ref, expansion_factor: chem.expansion_factor||'0.0006', vp_ref_temp: chem.vp_ref_temp||60 });
    setShowChemDD(false); setChemSearch('');
  };

  const aiLookup = async () => {
    if (!chemSearch) return;
    try {
      setAiLooking(true);
      const res = await api.post('/tanks/chemicals/lookup', { chemical_name: chemSearch });
      if (res.data.found) {
        setSelectedChem({ name: res.data.name, cas_number: res.data.cas_number, id: null });
        f({ chemical_id: res.data.id || '', chemical_name: res.data.name, molecular_weight: res.data.molecular_weight, liquid_density: res.data.liquid_density, tvp_at_bulk: res.data.vp_at_ref, expansion_factor: res.data.expansion_factor||'0.0006', vp_ref_temp: res.data.vp_ref_temp||60 });
        setShowChemDD(false);
        toast.success(`Properties found for \${res.data.name} — verify properties before use in permit applications`);
        setAiSourced(true);
      } else {
        toast.warn('Not found — enter properties manually');
        setShowCustomChem(true);
      }
    } catch(e) { toast.error('AI lookup failed'); }
    finally { setAiLooking(false); }
  };

  const saveCustomChem = async () => {
    try {
      const res = await api.post('/tanks/chemicals/custom', { name:form.cc_name, cas_number:form.cc_cas, molecular_weight:form.cc_mw, liquid_density:form.cc_density, vp_at_ref:form.cc_vp, vp_ref_temp:form.cc_vp_temp, expansion_factor:form.cc_expansion, tvp_a:form.cc_tvp_a||null, tvp_b:form.cc_tvp_b||null, tvp_c:form.cc_tvp_c||null });
      const chem = res.data.chemical;
      setChemicals(prev => [...prev, chem]);
      selectChem(chem);
      setShowCustomChem(false);
      toast.success('Custom chemical saved');
    } catch(e) { toast.error('Failed to save'); }
  };

  const calculate = async (save=false) => {
    try {
      setLoading(true);
      const payload = { ...form, save_calculation: save };
      const res = await api.post('/tanks/calculate', payload);
      setResults(res.data);
      if (save) { toast.success('Calculation saved'); loadInit(); }
      setStep(6);
    } catch(e) {
      toast.error('Calculation failed: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  const loadSaved = (calc) => {
    const inputs = calc.inputs_json || {};
    setForm(f => ({ ...EMPTY_FORM, ...inputs, tank_name: calc.tank_name, tank_type: calc.tank_type }));
    setResults(calc.results_json);
    setStep(6);
    setShowSaved(false);
  };

  const deleteSaved = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/tanks/calculations/${id}`);
    loadInit(); toast.success('Deleted');
  };

  const stepLabels = ['Tank Type','Identification','Characteristics','Liquid & Chemical','Operating Conditions','Results'];
  const totalSteps = 5;

  // ── Render ────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:3 }}>
            <FlaskConical size={20} color="#0066FF" />
            <h1 style={{ fontFamily:'var(--font-heading)', fontSize:20, fontWeight:800, color:'#0A1628', margin:0 }}>Tank Emissions Calculator</h1>
          </div>
          <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)' }}>AP-42 Chapter 7 · TANKS 5.3 equivalent · All 5 tank types</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => setShowSaved(!showSaved)} style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:7, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}>
            <Save size={13} /> Saved ({savedCalcs.length})
          </button>
          {step > 1 && (
            <button onClick={() => { setStep(1); setResults(null); setSelectedChem(null); setForm(EMPTY_FORM); }}
              style={{ padding:'7px 12px', borderRadius:7, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}>
              New Calculation
            </button>
          )}
        </div>
      </div>

      {/* Saved panel */}
      {showSaved && (
        <div className="card" style={{ marginBottom:18 }}>
          <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>Saved Calculations</div>
          {savedCalcs.length === 0 ? <div style={{ textAlign:'center', color:'#94a3b8', fontFamily:'var(--font-body)', fontSize:13, padding:'16px 0' }}>No saved calculations</div> :
            savedCalcs.map(calc => (
              <div key={calc.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid #F1F5F9' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)' }}>{calc.tank_name}</div>
                  <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>{calc.facility_name||'No facility'} · {(calc.tank_type||'').replace(/_/g,' ')} · {parseFloat(calc.total_loss_tons||0).toFixed(4)} ton/yr VOC</div>
                </div>
                <button onClick={() => loadSaved(calc)} style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #0066FF', background:'#EFF6FF', color:'#0066FF', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:12, fontWeight:600 }}>Load</button>
                <button onClick={() => deleteSaved(calc.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#DC2626', padding:3 }}><Trash2 size={13} /></button>
              </div>
            ))
          }
        </div>
      )}

      {/* Step indicator */}
      {step < 6 && (
        <div style={{ display:'flex', gap:0, marginBottom:22, borderRadius:9, overflow:'hidden' }}>
          {stepLabels.slice(0,5).map((title,i) => (
            <div key={i} style={{ flex:1, padding:'9px 6px', background: step===i+1?'#0066FF': step>i+1?'#E0EDFF':'#F8FAFC', borderRight:i<4?'1px solid white':'none', textAlign:'center' }}>
              <div style={{ fontSize:9, fontWeight:700, color: step===i+1?'white': step>i+1?'#0066FF':'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)' }}>Step {i+1}</div>
              <div style={{ fontSize:11, fontWeight:600, color: step===i+1?'white': step>i+1?'#0066FF':'#374151', fontFamily:'var(--font-body)' }}>{title}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── STEP 1: TANK TYPE ── */}
      {step === 1 && (
        <div>
          <SectionTitle>Select Tank Type</SectionTitle>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
            {TANK_TYPES.map(t => (
              <div key={t.value} onClick={() => f({ tank_type: t.value })}
                style={{ padding:'12px 14px', borderRadius:8, border:`1px solid ${form.tank_type===t.value?'#0066FF':'#E2E8F0'}`, borderLeft:`4px solid ${form.tank_type===t.value?'#0066FF':'#E2E8F0'}`, background: form.tank_type===t.value?'#EFF6FF':'white', cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color: form.tank_type===t.value?'#0066FF':'#0A1628', fontFamily:'var(--font-body)', marginBottom:2 }}>{t.label}</div>
                  <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)', lineHeight:1.5 }}>{t.desc}</div>
                </div>
                {form.tank_type===t.value && <div style={{ width:8, height:8, borderRadius:'50%', background:'#0066FF', flexShrink:0 }} />}
              </div>
            ))}
          </div>
          <button disabled={!form.tank_type} onClick={() => setStep(2)} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
            Next: Identification <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* ── STEP 2: IDENTIFICATION ── */}
      {step === 2 && (
        <div>
          <SectionTitle>Tank Identification</SectionTitle>
          <div style={grid2}>
            <Field label="Tank ID / Name *">
              <input type="text" value={form.tank_name} onChange={e => f({ tank_name: e.target.value })} placeholder="e.g. TK-001" style={inp} />
            </Field>
            <Field label="Facility (optional)">
              <select value={form.facility_id} onChange={e => f({ facility_id: e.target.value })} style={inp}>
                <option value="">Select facility</option>
                {facilities.map(fac => <option key={fac.id} value={fac.id}>{fac.facility_name||fac.name}</option>)}
              </select>
            </Field>
          </div>

          <SectionTitle>Meteorological Location</SectionTitle>
          <div style={grid2}>
            <Field label="State">
              <select value={form.location_state} onChange={e => onStateChange(e.target.value)} style={inp}>
                <option value="">Select state</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Nearest City (AP-42 Met Database)" hint="Auto-populated from state — change if needed">
              <select value={form.met_city} onChange={e => onCityChange(e.target.value)} style={inp}>
                <option value="">Select city</option>
                {metCities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          {form.avg_temp && (
            <>
              <SectionTitle>Meteorological Data — Override if Needed</SectionTitle>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14, marginBottom:14 }}>
                <Field label="Annual Avg Temperature (°F)"><input type="number" value={form.avg_temp} onChange={e => f({ avg_temp: e.target.value })} style={inp} /></Field>
                <Field label="Annual Max Temperature (°F)"><input type="number" value={form.max_temp} onChange={e => f({ max_temp: e.target.value })} style={inp} /></Field>
                <Field label="Annual Min Temperature (°F)"><input type="number" value={form.min_temp} onChange={e => f({ min_temp: e.target.value })} style={inp} /></Field>
                <Field label="Avg Daily Solar Insolation (BTU/ft²/day)"><input type="number" value={form.avg_solar_insolation} onChange={e => f({ avg_solar_insolation: e.target.value })} style={inp} /></Field>
                <Field label="Atmospheric Pressure (psia)"><input type="number" value={form.atmospheric_pressure} onChange={e => f({ atmospheric_pressure: e.target.value })} style={inp} /></Field>
              </div>
            </>
          )}

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => setStep(1)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14 }}>
              <ChevronLeft size={14} /> Back
            </button>
            <button disabled={!form.tank_name || !form.location_state} onClick={() => setStep(3)} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
              Next: Characteristics <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: CHARACTERISTICS ── */}
      {step === 3 && (
        <div>
          {/* VERTICAL FIXED ROOF */}
          {isVertical && (
            <>
              <SectionTitle>Tank Characteristics — Vertical Fixed Roof</SectionTitle>
              <div style={grid2}>
                <Field label="Tank Shape">
                  <select value={form.tank_shape} onChange={e => f({ tank_shape: e.target.value })} style={inp}>
                    <option value="cylinder">Cylinder</option>
                    <option value="rectangle">Rectangle</option>
                    <option value="square">Square</option>
                  </select>
                </Field>
                <Field label="Shell Diameter (ft) *"><input type="number" value={form.diameter} onChange={e => f({ diameter: e.target.value })} placeholder="e.g. 30" style={inp} /></Field>
                <Field label="Shell Height (ft) *"><input type="number" value={form.shell_height} onChange={e => f({ shell_height: e.target.value })} placeholder="e.g. 20" style={inp} /></Field>
                <Field label="Maximum Liquid Height (ft)" hint="Default: 90% of shell height"><input type="number" value={form.max_liquid_height} onChange={e => f({ max_liquid_height: e.target.value })} style={inp} /></Field>
                <Field label="Average Liquid Height (ft)" hint="Default: 50% of shell height"><input type="number" value={form.avg_liquid_height} onChange={e => f({ avg_liquid_height: e.target.value })} style={inp} /></Field>
              </div>

              <div style={{ ...grid2, marginTop:4 }}>
                <Field label="Tank Bottom Type">
                  <select value={form.bottom_type} onChange={e => f({ bottom_type: e.target.value })} style={inp}>
                    <option value="flat">Flat or Nominally Flat</option>
                    <option value="cone">Cone-Shaped Bottom</option>
                  </select>
                </Field>
                <Field label="Liquid Heel Type">
                  <select value={form.liquid_heel_type} onChange={e => f({ liquid_heel_type: e.target.value })} style={inp}>
                    <option value="no_heel">No Liquid Heel</option>
                    <option value="full_heel">Full Liquid Heel</option>
                    <option value="partial_heel">Partial Liquid Heel</option>
                  </select>
                </Field>
                {form.liquid_heel_type !== 'no_heel' && (
                  <Field label="Liquid Heel Height (ft)"><input type="number" value={form.liquid_heel_height} onChange={e => f({ liquid_heel_height: e.target.value })} style={inp} /></Field>
                )}
                <Field label="Vapor Space Pressure at Normal Operating Conditions (psig)" hint="Typical: 0 (atmospheric). Range 0 to 15 psig.">
                  <input type="number" value={form.vapor_space_pressure} onChange={e => f({ vapor_space_pressure: e.target.value })} style={inp} />
                </Field>
              </div>

              <SectionTitle>Tank Insulation & Heating</SectionTitle>
              <div style={grid2}>
                <Field label="Is Tank Insulated?">
                  <select value={form.is_insulated} onChange={e => f({ is_insulated: e.target.value })} style={inp}>
                    <option value="not_insulated">Not Insulated</option>
                    <option value="partially_insulated">Partially Insulated</option>
                    <option value="fully_insulated">Fully Insulated</option>
                  </select>
                </Field>
                {form.is_insulated === 'fully_insulated' && (
                  <Field label="Is Tank Heated?">
                    <select value={form.is_heated} onChange={e => f({ is_heated: e.target.value === 'true' })} style={inp}>
                      <option value="false">Not Heated</option>
                      <option value="true">Heated</option>
                    </select>
                  </Field>
                )}
                <Field label="Liquid Bulk Temperature Method">
                  <select value={form.bulk_temp_method} onChange={e => f({ bulk_temp_method: e.target.value })} style={inp}>
                    <option value="ap42">AP-42 Calculation (Equation 1-31)</option>
                    <option value="user_input">User Input</option>
                  </select>
                </Field>
                {form.bulk_temp_method === 'user_input' && (
                  <Field label="Liquid Bulk Temperature (°R)" hint="°R = °F + 459.67">
                    <input type="number" value={form.bulk_temp_rankine} onChange={e => f({ bulk_temp_rankine: e.target.value })} placeholder="e.g. 519.67 (= 60°F)" style={inp} />
                  </Field>
                )}
              </div>

              <SectionTitle>Shell Characteristics (AP-42 Table 7.1-6)</SectionTitle>
              <div style={grid2}>
                <Field label="Shell Color/Shade">
                  <select value={form.shell_color} onChange={e => f({ shell_color: e.target.value })} style={inp}>
                    {COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Shell Condition">
                  <select value={form.shell_condition} onChange={e => f({ shell_condition: e.target.value })} style={inp}>
                    {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
              </div>

              <SectionTitle>Roof Characteristics</SectionTitle>
              <div style={grid2}>
                <Field label="Roof Type">
                  <select value={form.roof_type} onChange={e => f({ roof_type: e.target.value })} style={inp}>
                    <option value="cone">Cone Roof</option>
                    <option value="dome">Dome Roof</option>
                    <option value="flat">Flat Roof</option>
                  </select>
                </Field>
                <Field label="Roof Color/Shade">
                  <select value={form.roof_color} onChange={e => f({ roof_color: e.target.value })} style={inp}>
                    {COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Roof Condition">
                  <select value={form.roof_condition} onChange={e => f({ roof_condition: e.target.value })} style={inp}>
                    {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                {form.roof_type === 'cone' && (
                  <Field label="Cone Roof Slope (ft/ft)" hint="Default: 0.0625 ft/ft if unknown">
                    <input type="number" value={form.roof_slope} onChange={e => f({ roof_slope: e.target.value })} style={inp} />
                  </Field>
                )}
                {form.roof_type === 'dome' && (
                  <Field label="Dome Roof Radius (ft)" hint="Typically 0.8× to 1.2× tank diameter. Default = shell diameter.">
                    <input type="number" value={form.roof_radius} onChange={e => f({ roof_radius: e.target.value })} placeholder={form.diameter || ''} style={inp} />
                  </Field>
                )}
              </div>

              <SectionTitle>Breather Vent Settings</SectionTitle>
              <div style={grid2}>
                <Field label="Vacuum Setting (psig)" hint="Between 0 and -1 psig. Default: -0.03">
                  <input type="number" value={form.vacuum_setting} onChange={e => f({ vacuum_setting: e.target.value })} style={inp} />
                </Field>
                <Field label="Pressure Setting (psig)" hint="Between 0 and 1 psig. Default: 0.03">
                  <input type="number" value={form.pressure_setting} onChange={e => f({ pressure_setting: e.target.value })} style={inp} />
                </Field>
              </div>

              <SectionTitle>Control Device</SectionTitle>
              <div style={grid2}>
                <Field label="Equipped with Control Device?">
                  <select value={form.has_control_device} onChange={e => f({ has_control_device: e.target.value === 'true' })} style={inp}>
                    <option value="false">No Control Device</option>
                    <option value="true">Yes — Control Device</option>
                  </select>
                </Field>
                {form.has_control_device && (
                  <Field label="Control Device Efficiency (%)">
                    <input type="number" value={form.control_device_efficiency} onChange={e => f({ control_device_efficiency: e.target.value })} placeholder="e.g. 95" style={inp} />
                  </Field>
                )}
              </div>
            </>
          )}

          {/* HORIZONTAL FIXED ROOF */}
          {isHorizontal && (
            <>
              <SectionTitle>Tank Characteristics — Horizontal Fixed Roof</SectionTitle>
              <div style={grid2}>
                <Field label="Shell Length (ft) *" hint="Length must be ≤ 6× diameter"><input type="number" value={form.shell_length} onChange={e => f({ shell_length: e.target.value })} style={inp} /></Field>
                <Field label="Shell Diameter (ft) *"><input type="number" value={form.diameter} onChange={e => f({ diameter: e.target.value })} style={inp} /></Field>
                <Field label="Maximum Liquid Height (ft) *"><input type="number" value={form.max_liquid_height} onChange={e => f({ max_liquid_height: e.target.value })} style={inp} /></Field>
                <Field label="Minimum Liquid Height (ft) *"><input type="number" value={form.min_liquid_height} onChange={e => f({ min_liquid_height: e.target.value })} style={inp} /></Field>
                <Field label="Vapor Space Pressure (psig)"><input type="number" value={form.vapor_space_pressure} onChange={e => f({ vapor_space_pressure: e.target.value })} style={inp} /></Field>
              </div>

              <SectionTitle>Insulation</SectionTitle>
              <div style={grid2}>
                <Field label="Is Tank Insulated or Underground?">
                  <select value={form.is_insulated} onChange={e => f({ is_insulated: e.target.value })} style={inp}>
                    <option value="not_insulated">Not Insulated, Not Underground</option>
                    <option value="partially_insulated">Partially Insulated</option>
                    <option value="fully_insulated">Fully Insulated or Underground</option>
                  </select>
                </Field>
                <Field label="Bulk Temp Method">
                  <select value={form.bulk_temp_method} onChange={e => f({ bulk_temp_method: e.target.value })} style={inp}>
                    <option value="ap42">AP-42 Calculation</option>
                    <option value="user_input">User Input</option>
                  </select>
                </Field>
                {form.bulk_temp_method === 'user_input' && (
                  <Field label="Liquid Bulk Temperature (°R)">
                    <input type="number" value={form.bulk_temp_rankine} onChange={e => f({ bulk_temp_rankine: e.target.value })} style={inp} />
                  </Field>
                )}
              </div>

              <SectionTitle>Shell Characteristics</SectionTitle>
              <div style={grid2}>
                <Field label="Shell Color"><select value={form.shell_color} onChange={e => f({ shell_color: e.target.value })} style={inp}>{COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
                <Field label="Shell Condition"><select value={form.shell_condition} onChange={e => f({ shell_condition: e.target.value })} style={inp}>{CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
              </div>

              <SectionTitle>Breather Vent & Control Device</SectionTitle>
              <div style={grid2}>
                <Field label="Vacuum Setting (psig)"><input type="number" value={form.vacuum_setting} onChange={e => f({ vacuum_setting: e.target.value })} style={inp} /></Field>
                <Field label="Pressure Setting (psig)"><input type="number" value={form.pressure_setting} onChange={e => f({ pressure_setting: e.target.value })} style={inp} /></Field>
                <Field label="Control Device?">
                  <select value={form.has_control_device} onChange={e => f({ has_control_device: e.target.value === 'true' })} style={inp}>
                    <option value="false">No</option><option value="true">Yes</option>
                  </select>
                </Field>
                {form.has_control_device && <Field label="Efficiency (%)"><input type="number" value={form.control_device_efficiency} onChange={e => f({ control_device_efficiency: e.target.value })} style={inp} /></Field>}
              </div>
            </>
          )}

          {/* INTERNAL FLOATING ROOF */}
          {isIFR && (
            <>
              <SectionTitle>Tank Characteristics — Internal Floating Roof</SectionTitle>
              <div style={grid2}>
                <Field label="Shell Diameter (ft) *"><input type="number" value={form.diameter} onChange={e => f({ diameter: e.target.value })} style={inp} /></Field>
                <Field label="Shell Height (ft) *"><input type="number" value={form.shell_height} onChange={e => f({ shell_height: e.target.value })} style={inp} /></Field>
                <Field label="Tank Bottom Type">
                  <select value={form.bottom_type} onChange={e => f({ bottom_type: e.target.value })} style={inp}>
                    <option value="flat">Flat or Nominally Flat</option>
                    <option value="cone">Cone-Shaped Bottom</option>
                  </select>
                </Field>
                <Field label="Liquid Heel Type">
                  <select value={form.liquid_heel_type} onChange={e => f({ liquid_heel_type: e.target.value })} style={inp}>
                    <option value="no_heel">No Liquid Heel</option>
                    <option value="full_heel">Full Liquid Heel</option>
                    <option value="partial_heel">Partial Liquid Heel</option>
                  </select>
                </Field>
                {form.liquid_heel_type !== 'no_heel' && <Field label="Liquid Heel Height (ft)"><input type="number" value={form.liquid_heel_height} onChange={e => f({ liquid_heel_height: e.target.value })} style={inp} /></Field>}
              </div>

              <SectionTitle>Shell Conditions</SectionTitle>
              <div style={grid2}>
                <Field label="Internal Shell Condition">
                  <select value={form.internal_shell_condition} onChange={e => f({ internal_shell_condition: e.target.value })} style={inp}>
                    {INTERNAL_SHELL_CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="External Shell Color"><select value={form.external_shell_color} onChange={e => f({ external_shell_color: e.target.value })} style={inp}>{COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
                <Field label="External Shell Condition"><select value={form.external_shell_condition} onChange={e => f({ external_shell_condition: e.target.value })} style={inp}>{CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
                <Field label="Roof Color"><select value={form.roof_color_float} onChange={e => f({ roof_color_float: e.target.value })} style={inp}>{COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
                <Field label="Roof Condition"><select value={form.roof_condition_float} onChange={e => f({ roof_condition_float: e.target.value })} style={inp}>{CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
              </div>

              <SectionTitle>Tank Construction & Rim Seal System</SectionTitle>
              <div style={grid2}>
                <Field label="Tank Construction">
                  <select value={form.tank_construction} onChange={e => f({ tank_construction: e.target.value })} style={inp}>
                    <option value="welded">Welded</option>
                    <option value="riveted">Riveted</option>
                  </select>
                </Field>
                <Field label="Self-Supporting Roof?" hint="Most converted tanks have columns">
                  <select value={form.self_supporting_roof} onChange={e => f({ self_supporting_roof: e.target.value === 'true' })} style={inp}>
                    <option value="false">No — Has Columns</option>
                    <option value="true">Yes — Self Supporting</option>
                  </select>
                </Field>
                {!form.self_supporting_roof && (
                  <>
                    <Field label="Number of Columns"><input type="number" value={form.num_columns} onChange={e => f({ num_columns: e.target.value })} style={inp} /></Field>
                    <Field label="Column Type">
                      <select value={form.column_type} onChange={e => f({ column_type: e.target.value })} style={inp}>
                        <option value="9x7_buildup">9×7 inch Built-up Column (1.1 ft dia)</option>
                        <option value="8in_pipe">8-inch Diameter Pipe (0.7 ft dia)</option>
                        <option value="unknown">Unknown (1.0 ft dia)</option>
                      </select>
                    </Field>
                  </>
                )}
                <Field label="Primary Seal">
                  <select value={form.seal_type} onChange={e => f({ seal_type: e.target.value })} style={inp}>
                    {PRIMARY_SEALS_IFR.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Secondary Seal">
                  <select value={form.secondary_seal} onChange={e => f({ secondary_seal: e.target.value })} style={inp}>
                    {SECONDARY_SEALS_IFR.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Seal Fit">
                  <select value={form.seal_fit} onChange={e => f({ seal_fit: e.target.value })} style={inp}>
                    <option value="average">Average-Fitting</option>
                    <option value="tight">Tight-Fitting (welded tanks only)</option>
                  </select>
                </Field>
              </div>

              <SectionTitle>Deck Type</SectionTitle>
              <div style={grid2}>
                <Field label="Deck Type">
                  <select value={form.deck_type} onChange={e => f({ deck_type: e.target.value })} style={inp}>
                    <option value="welded">Welded</option>
                    <option value="bolted">Bolted</option>
                  </select>
                </Field>
                {form.deck_type === 'bolted' && (
                  <>
                    <Field label="Deck Construction">
                      <select value={form.deck_construction} onChange={e => f({ deck_construction: e.target.value })} style={inp}>
                        <option value="sheet">Sheet</option>
                        <option value="panel">Panel</option>
                      </select>
                    </Field>
                    <Field label="Deck Seam">
                      <select value={form.deck_seam} onChange={e => f({ deck_seam: e.target.value })} style={inp}>
                        {form.deck_construction === 'sheet' ? <>
                          <option value="5ft">Sheet: 5 ft wide</option>
                          <option value="6ft">Sheet: 6 ft wide</option>
                          <option value="7ft">Sheet: 7 ft wide</option>
                        </> : <>
                          <option value="5x7.5">Panel: 5 × 7.5 ft</option>
                          <option value="5x12">Panel: 5 × 12 ft</option>
                        </>}
                      </select>
                    </Field>
                  </>
                )}
              </div>

              <SectionTitle>Deck Fittings (AP-42 Table 7.1-12)</SectionTitle>
              <div style={{ ...grid2, gridTemplateColumns:'repeat(2,1fr)' }}>
                {DECK_FITTINGS.map(df => (
                  <Field key={df.key} label={df.label}>
                    <input type="number" min="0" value={form.deck_fittings[df.key]||''} placeholder="0"
                      onChange={e => f({ deck_fittings: { ...form.deck_fittings, [df.key]: parseInt(e.target.value)||0 }})}
                      style={{ ...inp }} />
                  </Field>
                ))}
              </div>

              <SectionTitle>Bulk Liquid Temperature</SectionTitle>
              <div style={grid2}>
                <Field label="Bulk Temp Method">
                  <select value={form.bulk_temp_method} onChange={e => f({ bulk_temp_method: e.target.value })} style={inp}>
                    <option value="ap42">AP-42 Calculation (Equation 1-31)</option>
                    <option value="user_input">User Input</option>
                  </select>
                </Field>
                {form.bulk_temp_method === 'user_input' && (
                  <Field label="Liquid Bulk Temperature (°R)" hint="°R = °F + 459.67">
                    <input type="number" value={form.bulk_temp_rankine} onChange={e => f({ bulk_temp_rankine: e.target.value })} style={inp} />
                  </Field>
                )}
              </div>
            </>
          )}

          {/* EFR / DOMED EFR */}
          {isEFR && (
            <>
              <SectionTitle>Tank Characteristics — {form.tank_type === 'domed_external_floating' ? 'Domed ' : ''}External Floating Roof</SectionTitle>
              <div style={grid2}>
                <Field label="Shell Diameter (ft) *"><input type="number" value={form.diameter} onChange={e => f({ diameter: e.target.value })} style={inp} /></Field>
                <Field label="Shell Height (ft) *"><input type="number" value={form.shell_height} onChange={e => f({ shell_height: e.target.value })} style={inp} /></Field>
                <Field label="Tank Bottom Type">
                  <select value={form.bottom_type} onChange={e => f({ bottom_type: e.target.value })} style={inp}>
                    <option value="flat">Flat or Nominally Flat</option>
                    <option value="cone">Cone-Shaped Bottom</option>
                  </select>
                </Field>
                <Field label="Liquid Heel Type">
                  <select value={form.liquid_heel_type} onChange={e => f({ liquid_heel_type: e.target.value })} style={inp}>
                    <option value="no_heel">No Liquid Heel</option>
                    <option value="full_heel">Full Liquid Heel</option>
                    <option value="partial_heel">Partial Liquid Heel</option>
                  </select>
                </Field>
              </div>

              <SectionTitle>Shell & Roof Conditions</SectionTitle>
              <div style={grid2}>
                <Field label="Internal Shell Condition"><select value={form.internal_shell_condition} onChange={e => f({ internal_shell_condition: e.target.value })} style={inp}>{INTERNAL_SHELL_CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
                <Field label="External Shell Color"><select value={form.external_shell_color} onChange={e => f({ external_shell_color: e.target.value })} style={inp}>{COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
                <Field label="External Shell Condition"><select value={form.external_shell_condition} onChange={e => f({ external_shell_condition: e.target.value })} style={inp}>{CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
                <Field label="Roof Color"><select value={form.roof_color_float} onChange={e => f({ roof_color_float: e.target.value })} style={inp}>{COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
                <Field label="Roof Condition"><select value={form.roof_condition_float} onChange={e => f({ roof_condition_float: e.target.value })} style={inp}>{CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
              </div>

              <SectionTitle>Tank Construction & Rim Seal System</SectionTitle>
              <div style={grid2}>
                <Field label="Tank Construction">
                  <select value={form.tank_construction} onChange={e => f({ tank_construction: e.target.value, seal_type: e.target.value==='riveted'?'mechanical_shoe':form.seal_type })} style={inp}>
                    <option value="welded">Welded</option>
                    <option value="riveted">Riveted</option>
                  </select>
                </Field>
                <Field label="Primary Seal">
                  <select value={form.seal_type} onChange={e => f({ seal_type: e.target.value })} style={inp}>
                    {(form.tank_construction==='riveted' ? PRIMARY_SEALS_EFR_RIVETED : PRIMARY_SEALS_EFR_WELDED).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Secondary Seal">
                  <select value={form.secondary_seal} onChange={e => f({ secondary_seal: e.target.value })} style={inp}>
                    {SECONDARY_SEALS_EFR.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Seal Fit">
                  <select value={form.seal_fit} onChange={e => f({ seal_fit: e.target.value })} style={inp}>
                    <option value="average">Average-Fitting</option>
                    {form.tank_construction !== 'riveted' && <option value="tight">Tight-Fitting</option>}
                  </select>
                </Field>
              </div>

              <SectionTitle>Deck Characteristics</SectionTitle>
              <div style={grid2}>
                <Field label="Deck Type"><div style={{ ...inp, padding:'9px 12px', background:'#F8FAFC', color:'#374151' }}>Welded (EFR standard)</div></Field>
                <Field label="Roof Type (Pontoon vs Double-Deck)">
                  <select value={form.roof_type_float} onChange={e => f({ roof_type_float: e.target.value })} style={inp}>
                    <option value="pontoon">Pontoon Roof</option>
                    <option value="double_deck">Double-Deck Roof</option>
                  </select>
                </Field>
              </div>

              <SectionTitle>Deck Fittings (AP-42 Table 7.1-12)</SectionTitle>
              <div style={{ ...grid2, gridTemplateColumns:'repeat(2,1fr)' }}>
                {EFR_DECK_FITTINGS.map(df => (
                  <Field key={df.key} label={df.label}>
                    <input type="number" min="0" value={form.deck_fittings[df.key]||''} placeholder="0"
                      onChange={e => f({ deck_fittings: { ...form.deck_fittings, [df.key]: parseInt(e.target.value)||0 }})}
                      style={inp} />
                  </Field>
                ))}
              </div>

              <SectionTitle>Bulk Liquid Temperature</SectionTitle>
              <div style={grid2}>
                <Field label="Bulk Temp Method">
                  <select value={form.bulk_temp_method} onChange={e => f({ bulk_temp_method: e.target.value })} style={inp}>
                    <option value="ap42">AP-42 Calculation</option>
                    <option value="user_input">User Input</option>
                  </select>
                </Field>
                {form.bulk_temp_method === 'user_input' && (
                  <Field label="Liquid Bulk Temperature (°R)">
                    <input type="number" value={form.bulk_temp_rankine} onChange={e => f({ bulk_temp_rankine: e.target.value })} style={inp} />
                  </Field>
                )}
              </div>
            </>
          )}

          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <button onClick={() => setStep(2)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14 }}>
              <ChevronLeft size={14} /> Back
            </button>
            <button disabled={!form.diameter} onClick={() => setStep(4)} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
              Next: Liquid & Chemical <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: LIQUID & CHEMICAL ── */}
      {step === 4 && (
        <div>
          <SectionTitle>Chemical / Liquid Stored</SectionTitle>

          {selectedChem ? (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, marginBottom:8 }}>
                <CheckCircle size={16} color="#16A34A" />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#15803D', fontFamily:'var(--font-body)' }}>{selectedChem.name}</div>
                  {selectedChem.cas_number && <div style={{ fontSize:11, color:'#16A34A', fontFamily:'var(--font-body)' }}>CAS: {selectedChem.cas_number}</div>}
                </div>
                <button onClick={() => { setSelectedChem(null); setAiSourced(false); f({ chemical_id:'', chemical_name:'', molecular_weight:'', liquid_density:'', tvp_at_bulk:'', expansion_factor:'', vp_ref_temp:'60' }); }}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'#6B7280', fontSize:12, fontFamily:'var(--font-body)' }}>Change</button>
              </div>
              {aiSourced && <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', fontStyle:'italic', marginBottom:12 }}>AI-sourced · Verify properties against AP-42 Appendix A before use in permit applications</div>}
            </>
          ) : (
            <div style={{ marginBottom:16 }}>
              <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                <input value={chemSearch} onChange={e => { setChemSearch(e.target.value); setShowChemDD(true); }}
                  onFocus={() => setShowChemDD(true)}
                  placeholder="Search chemical library (gasoline, benzene, toluene, MEK...)"
                  style={{ ...inp, flex:1 }} />
                <button onClick={aiLookup} disabled={!chemSearch || aiLooking}
                  style={{ padding:'9px 12px', borderRadius:8, border:'1px solid #0066FF', background:'#EFF6FF', color:'#0066FF', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}>
                  <RefreshCw size={12} style={{ animation: aiLooking?'spin 1s linear infinite':'none' }} />
                  {aiLooking ? 'Looking up...' : 'AI Lookup'}
                </button>
              </div>
              {showChemDD && chemSearch && (
                <div style={{ background:'white', border:'1px solid #E2E8F0', borderRadius:8, maxHeight:200, overflowY:'auto', marginBottom:10 }}>
                  {filteredChems.slice(0,20).map(c => (
                    <div key={c.id} onClick={() => selectChem(c)}
                      style={{ padding:'8px 14px', cursor:'pointer', borderBottom:'1px solid #F1F5F9', display:'flex', justifyContent:'space-between' }}
                      onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                      onMouseLeave={e => e.currentTarget.style.background='white'}>
                      <div>
                        <div style={{ fontSize:13, fontWeight:500, color:'#0A1628', fontFamily:'var(--font-body)' }}>{c.name}</div>
                        <div style={{ fontSize:10, color:'#94a3b8', fontFamily:'var(--font-body)' }}>{c.cas_number||''} · {c.category}</div>
                      </div>
                      <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)', textAlign:'right' }}>
                        <div>MW: {c.molecular_weight}</div>
                        <div>VP: {c.vp_at_ref} psia</div>
                      </div>
                    </div>
                  ))}
                  {filteredChems.length === 0 && <div style={{ padding:'12px 14px', color:'#94a3b8', fontSize:13, fontFamily:'var(--font-body)', textAlign:'center' }}>Not found — try AI Lookup or add manually</div>}
                </div>
              )}
              <button onClick={() => setShowCustomChem(!showCustomChem)}
                style={{ display:'flex', alignItems:'center', gap:5, background:'none', border:'none', cursor:'pointer', color:'#0066FF', fontFamily:'var(--font-body)', fontSize:13, fontWeight:600, padding:0 }}>
                <Plus size={12} /> Add custom chemical manually
              </button>
            </div>
          )}

          {showCustomChem && (
            <div className="card" style={{ padding:16, marginBottom:16, border:'1px solid #0066FF' }}>
              <SectionTitle>Custom Chemical Properties (AP-42 Table 7.1-3 format)</SectionTitle>
              <div style={grid2}>
                <Field label="Chemical Name *"><input type="text" value={form.cc_name} onChange={e => f({ cc_name: e.target.value })} style={inp} /></Field>
                <Field label="CAS Number"><input type="text" value={form.cc_cas} onChange={e => f({ cc_cas: e.target.value })} placeholder="Optional" style={inp} /></Field>
                <Field label="Molecular Weight (lb/lb-mol) *"><input type="number" value={form.cc_mw} onChange={e => f({ cc_mw: e.target.value })} style={inp} /></Field>
                <Field label="Liquid Density (lb/gal at 60°F) *"><input type="number" value={form.cc_density} onChange={e => f({ cc_density: e.target.value })} style={inp} /></Field>
                <Field label="Vapor Pressure at Reference Temp (psia) *"><input type="number" value={form.cc_vp} onChange={e => f({ cc_vp: e.target.value })} style={inp} /></Field>
                <Field label="Reference Temp (°F)"><input type="number" value={form.cc_vp_temp} onChange={e => f({ cc_vp_temp: e.target.value })} style={inp} /></Field>
                <Field label="Liquid Expansion Factor (gal/gal/°F)" hint="Typical: 0.0004–0.0009"><input type="number" value={form.cc_expansion} onChange={e => f({ cc_expansion: e.target.value })} style={inp} /></Field>
              </div>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:13, fontWeight:700, color:'#374151', marginBottom:8 }}>Antoine Equation Constants (optional — for more accurate TVP)</div>
              <div style={grid3}>
                <Field label="Antoine A" hint="log10(P_mmHg) = A - B/(T_°C + C)"><input type="number" value={form.cc_tvp_a} onChange={e => f({ cc_tvp_a: e.target.value })} style={inp} /></Field>
                <Field label="Antoine B (°C)"><input type="number" value={form.cc_tvp_b} onChange={e => f({ cc_tvp_b: e.target.value })} style={inp} /></Field>
                <Field label="Antoine C (°C)"><input type="number" value={form.cc_tvp_c} onChange={e => f({ cc_tvp_c: e.target.value })} style={inp} /></Field>
              </div>
              <button onClick={saveCustomChem} className="btn btn-primary" style={{ fontSize:13 }}>Save & Use Chemical</button>
            </div>
          )}

          {selectedChem && (
            <>
              <SectionTitle>Chemical Properties — Override if Needed</SectionTitle>
              <div style={grid3}>
                <Field label="Molecular Weight (lb/lb-mol)"><input type="number" value={form.molecular_weight} onChange={e => f({ molecular_weight: e.target.value })} style={inp} /></Field>
                <Field label="Liquid Density (lb/gal at 60°F)"><input type="number" value={form.liquid_density} onChange={e => f({ liquid_density: e.target.value })} style={inp} /></Field>
                <Field label="TVP at Bulk Liquid Temp (psia)" hint="True vapor pressure at estimated bulk liquid temperature"><input type="number" value={form.tvp_at_bulk} onChange={e => f({ tvp_at_bulk: e.target.value })} style={inp} /></Field>
              </div>
            </>
          )}

          <SectionTitle>Throughput</SectionTitle>
          <div style={grid2}>
            <Field label="Input Method">
              <select value={form.input_method} onChange={e => f({ input_method: e.target.value })} style={inp}>
                <option value="annual">Annual Values</option>
                <option value="monthly">Monthly Values</option>
              </select>
            </Field>
            {form.input_method === 'annual' && (
              <Field label="Annual Throughput (gal/yr)" hint="Total liquid pumped in and out per year. Enter 0 if constant level tank.">
                <input type="number" value={form.annual_throughput} onChange={e => f({ annual_throughput: e.target.value })} placeholder="e.g. 500000" style={inp} />
              </Field>
            )}
          </div>
          {form.input_method === 'monthly' && (
            <>
              <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)', marginBottom:10 }}>Enter monthly throughput in gallons:</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:14 }}>
                {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => (
                  <Field key={m} label={m}>
                    <input type="number" value={form[`month_${i}`]||''} onChange={e => f({ [`month_${i}`]: e.target.value })} placeholder="gal" style={inp} />
                  </Field>
                ))}
              </div>
            </>
          )}

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => setStep(3)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14 }}>
              <ChevronLeft size={14} /> Back
            </button>
            <button disabled={!selectedChem && !form.molecular_weight} onClick={() => setStep(5)} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
              Next: Review & Calculate <ChevronRight size={14} />
            </button>
          </div>
          <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
        </div>
      )}

      {/* ── STEP 5: REVIEW & CALCULATE ── */}
      {step === 5 && (
        <div>
          <SectionTitle>Review & Calculate</SectionTitle>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10, marginBottom:20 }}>
            {[
              { label:'Tank ID', value: form.tank_name },
              { label:'Tank Type', value: TANK_TYPES.find(t=>t.value===form.tank_type)?.label },
              { label:'Location', value: form.met_city || form.location_state },
              { label:'Chemical', value: form.chemical_name || selectedChem?.name || 'Not selected' },
              { label:'Diameter', value: form.diameter ? `${form.diameter} ft` : '—' },
              { label:'Shell Height', value: form.shell_height ? `${form.shell_height} ft` : '—' },
              { label:'Annual Throughput', value: form.annual_throughput ? `${parseFloat(form.annual_throughput).toLocaleString()} gal/yr` : '0 gal/yr' },
              { label:'Met Data City', value: form.met_city || 'Not set' },
            ].map((item,i) => (
              <div key={i} style={{ padding:'9px 12px', background:'#F8FAFC', borderRadius:7 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)', marginBottom:2 }}>{item.label}</div>
                <div style={{ fontSize:13, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)' }}>{item.value||'—'}</div>
              </div>
            ))}
          </div>

          <Field label="Notes / Comments">
            <textarea value={form.notes} onChange={e => f({ notes: e.target.value })} rows={2} placeholder="Optional calculation notes..." style={{ ...inp, resize:'vertical' }} />
          </Field>

          <div style={{ display:'flex', gap:10, marginTop:14, flexWrap:'wrap' }}>
            <button onClick={() => setStep(4)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14 }}>
              <ChevronLeft size={14} /> Back
            </button>
            <button onClick={() => calculate(false)} disabled={loading} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
              <Calculator size={14} /> {loading ? 'Calculating...' : 'Calculate Emissions'}
            </button>
            <button onClick={() => calculate(true)} disabled={loading}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:8, border:'1px solid #16A34A', background:'#F0FDF4', color:'#16A34A', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14, fontWeight:600 }}>
              <Save size={14} /> Calculate & Save
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 6: RESULTS ── */}
      {step === 6 && results && (
        <div ref={printRef}>
          <style>{`
            @media print {
              .no-print { display:none !important; }
              body { background:white !important; }
              .card { box-shadow:none !important; }
            }
          `}</style>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }} className="no-print">
            <div style={{ fontFamily:'var(--font-heading)', fontSize:16, fontWeight:700, color:'#0A1628' }}>Calculation Results</div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => window.print()} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}>
                <Printer size={13} /> Print / PDF
              </button>
              <button onClick={() => calculate(true)} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:8, border:'1px solid #16A34A', background:'#F0FDF4', color:'#16A34A', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13, fontWeight:600 }}>
                <Save size={13} /> Save
              </button>
            </div>
          </div>

          {/* Report Header */}
          <div className="card" style={{ padding:20, marginBottom:14 }}>
            <div style={{ textAlign:'center', marginBottom:16, paddingBottom:14, borderBottom:'2px solid #0066FF' }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:18, fontWeight:800, color:'#0066FF', marginBottom:3 }}>AirComply — Storage Tank Emission Report</div>
              <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)' }}>
                AP-42 Chapter 7.1 — Organic Liquid Storage Tanks · Equivalent to EPA TANKS 5.3 · {new Date().toLocaleDateString()}
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
              {[
                { label:'Tank ID', value: form.tank_name },
                { label:'Tank Type', value: TANK_TYPES.find(t=>t.value===form.tank_type)?.label },
                { label:'Facility', value: facilities.find(fac=>fac.id==form.facility_id)?.facility_name||'Not specified' },
                { label:'Chemical / Product', value: results.chemical_used||form.chemical_name },
                { label:'Meteorological Location', value: results.met_used?.city || form.met_city },
                { label:'Calculation Method', value: 'AP-42 Chapter 7.1 (5th Edition)' },
                { label:'Reference Software', value: 'Equivalent to EPA TANKS 5.3' },
                { label:'Calculation Date', value: new Date().toLocaleDateString() },
              ].map((item,i) => (
                <div key={i} style={{ padding:'7px 10px', background:'#F8FAFC', borderRadius:7 }}>
                  <div style={{ fontSize:9, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)', marginBottom:2 }}>{item.label}</div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)' }}>{item.value||'—'}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Main results */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
            {[
              { label:'Standing Loss (Breathing)', value: parseFloat(results.standing_loss||0).toFixed(2), unit:'lb/yr', color:'#D97706', bg:'#FFFBEB' },
              { label:'Working Loss (Filling)', value: parseFloat(results.working_loss||0).toFixed(2), unit:'lb/yr', color:'#0066FF', bg:'#EFF6FF' },
              { label:'Total VOC Emissions', value: parseFloat(results.total_loss_tons||0).toFixed(4), unit:'ton/yr', color: parseFloat(results.total_loss_tons||0)>1?'#DC2626':'#16A34A', bg: parseFloat(results.total_loss_tons||0)>1?'#FEF2F2':'#F0FDF4', big:true },
            ].map((s,i) => (
              <div key={i} className="card" style={{ padding:'16px 18px', background:s.bg, border:'none', textAlign:'center' }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)', marginBottom:6 }}>{s.label}</div>
                <div style={{ fontFamily:'var(--font-heading)', fontSize:s.big?28:22, fontWeight:800, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11, color:s.color, fontFamily:'var(--font-body)', fontWeight:600 }}>{s.unit}</div>
              </div>
            ))}
          </div>

          {/* Emission table */}
          <div className="card" style={{ padding:'18px 20px', marginBottom:14 }}>
            <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>Emission Summary</div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#F8FAFC' }}>
                  {['Emission Component','lb/yr','ton/yr','kg/yr'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', fontFamily:'var(--font-body)', textTransform:'uppercase', borderBottom:'1px solid #E2E8F0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { name:'Standing Loss (Breathing Loss)', val:results.standing_loss },
                  { name:'Working Loss (Filling/Withdrawal Loss)', val:results.working_loss },
                  { name:'Total VOC Emissions', val:results.total_loss, bold:true },
                ].map((row,i) => (
                  <tr key={i} style={{ borderBottom:'1px solid #F8FAFC', background:row.bold?'#F0FDF4':'white' }}>
                    <td style={{ padding:'9px 12px', fontSize:13, fontFamily:'var(--font-body)', fontWeight:row.bold?700:400, color:'#0A1628' }}>{row.name}</td>
                    <td style={{ padding:'9px 12px', fontSize:13, fontFamily:'var(--font-body)', fontWeight:row.bold?700:400, color:row.bold?'#16A34A':'#374151' }}>{parseFloat(row.val||0).toFixed(2)}</td>
                    <td style={{ padding:'9px 12px', fontSize:13, fontFamily:'var(--font-body)', fontWeight:row.bold?700:400, color:row.bold?'#16A34A':'#374151' }}>{(parseFloat(row.val||0)/2000).toFixed(6)}</td>
                    <td style={{ padding:'9px 12px', fontSize:13, fontFamily:'var(--font-body)', fontWeight:row.bold?700:400, color:row.bold?'#16A34A':'#374151' }}>{(parseFloat(row.val||0)*0.453592).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Intermediate calculations */}
          {results.intermediate && Object.keys(results.intermediate).length > 0 && (
            <div className="card" style={{ padding:'18px 20px', marginBottom:14 }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>Intermediate Calculation Values</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                {Object.entries(results.intermediate).map(([key,val],i) => (
                  <div key={i} style={{ padding:'8px 10px', background:'#F8FAFC', borderRadius:7 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.4px', fontFamily:'var(--font-body)', marginBottom:2 }}>{key}</div>
                    <div style={{ fontSize:12, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)' }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Met data used */}
          <div className="card" style={{ padding:'18px 20px', marginBottom:14 }}>
            <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>Meteorological Data Used</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
              {[
                { label:'Location', value: results.met_used?.city || form.met_city },
                { label:'Avg Temp', value: `${results.met_used?.avg}°F` },
                { label:'Max Temp', value: `${results.met_used?.max}°F` },
                { label:'Min Temp', value: `${results.met_used?.min}°F` },
                { label:'Solar Insolation', value: `${results.met_used?.solar} BTU/ft²/day` },
                { label:'Atm Pressure', value: `${results.met_used?.pressure} psia` },
              ].map((item,i) => (
                <div key={i} style={{ padding:'8px 10px', background:'#F8FAFC', borderRadius:7 }}>
                  <div style={{ fontSize:9, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', fontFamily:'var(--font-body)', marginBottom:2 }}>{item.label}</div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)' }}>{item.value||'—'}</div>
                </div>
              ))}
            </div>
          </div>

          {/* AI sourced disclaimer — based on backend flag, persists across sessions */}
          {results.ai_sourced && (
            <div style={{ padding:'8px 14px', background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:7, marginBottom:10, fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', fontStyle:'italic' }}>
              Chemical properties sourced via AI lookup. Verify against AP-42 Appendix A before use in permit applications or regulatory submittals.
            </div>
          )}
          {/* Disclaimer */}
          <div style={{ padding:'12px 16px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, marginBottom:12 }}>
            <div style={{ fontSize:11, color:'#92400E', fontFamily:'var(--font-body)', lineHeight:1.7 }}>
              <strong>Disclaimer:</strong> Emissions calculated using AP-42 Chapter 7.1 equations (5th Edition, U.S. EPA, 2020 revision). Calculations are equivalent to EPA TANKS 5.3 methodology. Results represent screening-level estimates for routine standing and working losses only; landing losses, cleaning losses, and flashing emissions are not included. User is responsible for accuracy of input parameters. Results should be reviewed by a licensed Professional Engineer (PE) before use in permit applications, regulatory submittals, or Title V compliance demonstrations.
            </div>
          </div>

          {/* Regulatory reference */}
          <div style={{ padding:'12px 16px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8 }}>
            <div style={{ fontSize:11, color:'#1E40AF', fontFamily:'var(--font-body)', lineHeight:1.7 }}>
              <strong>Regulatory Reference:</strong> {results.regulatory_citation || 'U.S. EPA AP-42, 5th Edition, Chapter 7.1 — Organic Liquid Storage Tanks'}. Emission factor equations developed by the American Petroleum Institute (API). For applicability thresholds: Title V major source = 100 tpy criteria pollutants; NSR/PSD major modification = 40 tpy VOC (attainment areas); NSPS Subpart Kb — volatile organic liquid storage vessels ≥151 m³ capacity.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
